import { useEffect, useRef, useState } from 'react'
import { TerminalViewLazy as TerminalView } from '../terminal/TerminalViewLazy'
import { ARROW } from '../terminal/useDpad'
import { Sheet } from '../ui/Sheet'
import type { AgentRow } from '../transport/api'
import type { TerminalSocket } from '../transport/ws'

/**
 * Answer an agent's prompt without leaving Clean View.
 *
 * An agent CLI that stops to ask something is reading keys as MENU NAVIGATION. In a
 * rich view the question is not rendered at all, so the user is being asked something
 * they cannot see, by a process that is eating whatever they type. Switching to the
 * terminal answers it — and costs them the view they were reading, which is the one
 * that told them what the agent had been doing.
 *
 * So the terminal comes to them instead: the real pane, live, over the question only.
 *
 * WHY A SECOND SOCKET RATHER THAN THE HEADLESS ONE. A rich view already holds a
 * write-only socket for the owner's typing, but it is deliberately deaf — no `onData`
 * — because a transcript view renders from HTTP and has no use for the byte stream.
 * Showing the pane needs those bytes. `TerminalView` owns its socket end to end
 * (creates, attaches, and nulls the ref on unmount), so handing it a ref of its own
 * leaves the page's `termSock` untouched and the sheet cleans up after itself.
 *
 * The three ways in are the three the question can want: a direction, a key, or
 * words. A selection menu wants ↑/↓ then Enter; a "type y to confirm" wants a
 * character; a free-text question wants a sentence, and on a phone that is far
 * easier said than typed.
 */
export function PromptSheet({ agent, onClose }: { agent: AgentRow; onClose: () => void }) {
  const sock = useRef<TerminalSocket | null>(null)
  const [text, setText] = useState('')
  const [note, setNote] = useState<string | null>(null)
  /*
   * The sheet's TerminalView TAKES OVER the session (it is where the eyes
   * are), which freezes the main terminal behind it. Closing the sheet must
   * hand the picture BACK — without this, every composer use left the main
   * view frozen under a tap (owner: 回去 100% 空白). The event is same-page
   * by construction; a frozen view whose page is visible retakes on it.
   */
  useEffect(
    () => () => {
      window.dispatchEvent(new Event('wt-retake'))
    },
    [],
  )
  /**
   * Attaching is not instant, and an empty pane looks exactly like a broken one.
   *
   * A fresh `/ws` is a plain login shell; `attach()` waits a deliberate 300ms for the
   * profile to finish sourcing before typing `tmux attach`, so for the first half
   * second there is genuinely nothing to show. Without a word for that state the sheet
   * opens black, and black is what "it doesn't work" looks like.
   */
  const [link, setLink] = useState<'connecting' | 'attached' | 'closed'>('connecting')

  const send = (s: string) => {
    if (!sock.current?.ready) {
      setNote('terminal not connected yet')
      return
    }
    sock.current.write(s)
  }

  /**
   * Dictation writes into the FIELD, not into the pane.
   *
   * The transcript is a guess, and an agent's prompt is usually a decision — "yes",
   * "no", a filename. Speaking straight into the pane would make a misheard word an
   * answer, with no step at which anyone could see it was wrong. Same rule the rest
   * of the app follows: dispatched into the composer, not sent.
   */
  const submitText = () => {
    const body = text.trim()
    if (!body) return
    send(body)
    setText('')
  }

  const KEYS: { label: string; seq: string; title: string }[] = [
    { label: '↑', seq: ARROW.up, title: 'Up' },
    { label: '↓', seq: ARROW.down, title: 'Down' },
    { label: '←', seq: ARROW.left, title: 'Left' },
    { label: '→', seq: ARROW.right, title: 'Right' },
  ]

  return (
    <Sheet title={`Answer ${agent.session}`} onClose={onClose} className="prompt-sheet">
      {/* The pane itself, live. Nothing here interprets the question — the agent's own
          rendering is the only thing that can be trusted to say what it is asking. */}
      <div className="ps-term">
        {/* This DOES size the session, and the earlier decision not to was wrong.
          *
          * It ran `passive` — never resizing — so a phone opening this sheet could
          * not reshape a terminal someone was reading on a desktop. The predicted
          * harm was real; the harm it caused was worse and immediate. A pty that is
          * still drawing for the old width repaints into a grid that does not match,
          * so cursor moves land in the wrong cells and nothing overwrites what was
          * there: the owner's screenshot shows the same menu painted three times,
          * pointer in a different place each time. An unreadable pane defeats the
          * only thing this sheet is for.
          *
          * The trade is now the right way round, and it is bounded: tmux 3.4 runs
          * `window-size latest`, so a desktop that resizes here is resized back the
          * moment its own client is active again. Observed harm beats predicted
          * harm. */}
        <TerminalView
          session={agent.session}
          host={agent.host}
          // The agent's own machine — the sheet answers THIS agent's prompt, and
          // the ambient relay may be addressing another pane's (2026-08-28).
          device={agent.device_id ?? null}
          socketRef={sock}
          onLink={setLink}
        />
        {link !== 'attached' && (
          <div className="ps-term-veil">
            {link === 'connecting' ? 'Attaching to the session…' : 'Terminal disconnected'}
          </div>
        )}
      </div>

      <div className="ps-keys">
        {KEYS.map((k) => (
          <button key={k.label} type="button" title={k.title} onClick={() => send(k.seq)}>
            {k.label}
          </button>
        ))}
        {/* Enter is separated and wider: it is the one that COMMITS, and on a phone a
            mis-tap between "move the selection" and "choose it" is the difference
            between browsing and answering. */}
        <button type="button" className="ps-enter" title="Enter" onClick={() => send('\r')}>
          ⏎ Enter
        </button>
        <button type="button" title="Escape" onClick={() => send('\x1b')}>
          Esc
        </button>
      </div>

      <div className="ps-row">
        <input
          className="ps-input"
          value={text}
          placeholder="Type an answer…"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submitText()
            }
          }}
        />
        <button type="button" className="ps-send" onClick={submitText} disabled={!text.trim()}>
          Send
        </button>
      </div>

      {/* Typed text is sent WITHOUT a trailing Enter, so it can be read back in the
          pane above before it is committed — the same two-stage rule the main
          composer follows for agents. */}
      <div className="ps-note">
        {note ??
          (link === 'attached'
            ? 'Text is typed into the pane; press Enter to commit it.'
            : 'Waiting for the pane — the shell is still starting up.')}
      </div>
    </Sheet>
  )
}
